import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getInitialIppAssessmentRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface InitialIppAssessmentRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

interface InitialIppAssessmentItem {
  label: string;
  value: string | undefined;
}

export default function InitialIppAssessmentRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  onRefresh,
  isViewOnly,
}: InitialIppAssessmentRowProps) {
  const { alert, management, bleeding, notes, hasAnswers, hasNotes } = useMemo(
    () =>
      observation
        ? getInitialIppAssessmentRowValues(observation)
        : {
            alert: undefined,
            management: undefined,
            bleeding: undefined,
            notes: undefined,
            hasAnswers: false,
            hasNotes: false,
          },
    [observation],
  );

  const assessmentItems = useMemo<InitialIppAssessmentItem[]>(
    () => [
      { label: "Alert", value: alert },
      { label: "Active Management Initiated", value: management },
      { label: "Bleeding", value: bleeding },
    ],
    [alert, management, bleeding],
  );

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Initial IPP Assessment:</TextWrapper>
            <List fz="sm" withPadding>
              {assessmentItems.map(
                (item) =>
                  item.value && (
                    <List.Item key={`${item.label}-${item.value}`}>
                      <Group gap="xs">
                        <TextWrapper size="sm" variant="bold">
                          {item.label}:
                        </TextWrapper>
                        <TextWrapper size="sm">{item.value}</TextWrapper>
                      </Group>
                    </List.Item>
                  ),
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Initial IPP Assessment Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={InitialIppAssessmentRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
